import { Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { MenuModule, Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { OrderService } from '../../core/services/order.service';
import { StoreService } from '../../core/services/store.service';
import { OrderDto, OrderDetailDto, ProfitReportDto } from '../../core/models/order.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { ReturnDialogComponent } from './return-dialog.component';
import { InputTextModule } from 'primeng/inputtext';
import {
  ReceiptComponent,
  ReceiptData,
  ReceiptPaymentType,
} from '../../shared/components/receipt/receipt.component';
import { ReceiptPrintService } from '../../shared/services/receipt-print.service';
import { ReceiptExportService } from '../../shared/services/receipt-export.service';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    MultiSelectModule,
    TagModule,
    DialogModule,
    DatePickerModule,
    MenuModule,
    TranslatePipe,
    ReturnDialogComponent,
    InputTextModule,
    ReceiptComponent,
  ],
  templateUrl: './orders.component.html',
})
export class OrdersComponent implements OnInit {
  private orderSvc = inject(OrderService);
  private storeSvc = inject(StoreService);
  readonly currencySvc = inject(CurrencyService);
  private translate = inject(TranslateService);
  private authSvc = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private receiptPrinter = inject(ReceiptPrintService);
  private receiptExport = inject(ReceiptExportService);

  // ── Table state ────────────────────────────────────────────────────────────
  orders = signal<OrderDto[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  pageSize = 20;

  // ── Profit report ──────────────────────────────────────────────────────────
  report = signal<ProfitReportDto | null>(null);

  /**
   * Foyda hisoboti qaysi valyuta daftaridan o'qiladi. Bo'sh — backend kotirovka
   * valyutasini oladi; yorliq uchun `reportCurrency()` shuni ko'rsatadi.
   */
  selectedReportCurrency = signal('');
  reportCurrency = computed(() => this.selectedReportCurrency() || this.currencySvc.defaultCode());
  reportLoading = signal(false);

  // ── Filters ────────────────────────────────────────────────────────────────
  searchQuery = '';
  filterStoreIds: string[] = [];
  filterPaymentType: string | null = null;
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  // ── Lookup ─────────────────────────────────────────────────────────────────
  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);

  get paymentTypes() {
    return [
      { label: this.translate.instant('ORDERS.PAYMENT_TYPES.CASH'), value: 'Cash' },
      { label: this.translate.instant('ORDERS.PAYMENT_TYPES.CARD'), value: 'Card' },
      { label: this.translate.instant('ORDERS.PAYMENT_TYPES.DEBT'), value: 'Debt' },
    ];
  }

  // ── Row menu ───────────────────────────────────────────────────────────────
  rowMenuItems: MenuItem[] = [];

  // ── Details dialog ─────────────────────────────────────────────────────────
  detailsVisible = false;
  detailsLoading = false;
  selectedOrder = signal<OrderDetailDto | null>(null);
  receiptVisible = signal(false);
  receipt = signal<ReceiptData | null>(null);
  receiptOrderId = signal<string | null>(null);
  exportBusy = signal(false);
  shareBusy = signal(false);

  // ── Return dialog ──────────────────────────────────────────────────────────
  returnVisible = signal(false);
  returnOrder = signal<OrderDetailDto | null>(null);

  private lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };
  private storesLoaded = false;

  ngOnInit() {
    const now = new Date();
    this.filterFrom = new Date(now.getFullYear(), now.getMonth(), 1);
    this.filterTo = now;
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const storeList = res.data ?? [];
          this.stores.set(storeList);
          if (this.authSvc.user()?.role === 'Employee' && storeList.length > 0) {
            this.filterStoreIds = [storeList[0].id];
          }
          this.storesLoaded = true;
          this.loadReport();
          this.loadOrders(this.lastLazyEvent);
        },
      });
  }

  loadOrders(event: TableLazyLoadEvent) {
    this.lastLazyEvent = event;
    if (!this.storesLoaded) return;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    const size = event.rows ?? this.pageSize;
    this.loading.set(true);

    this.orderSvc
      .getOrders(
        page,
        size,
        this.filterStoreIds.length ? this.filterStoreIds : undefined,
        this.filterPaymentType ?? undefined,
        this.toIso(this.filterFrom),
        this.toIso(this.filterTo),
        this.searchQuery || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.orders.set(res.data?.items ?? []);
          this.totalCount.set(res.data?.totalCount ?? 0);
        },
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });
  }

  loadReport() {
    if (!this.filterFrom || !this.filterTo) return;
    this.reportLoading.set(true);
    this.orderSvc
      .getProfitReport(
        this.toIso(this.filterFrom)!,
        this.toIso(this.filterTo)!,
        this.filterStoreIds.length ? this.filterStoreIds : undefined,
        this.selectedReportCurrency() || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.report.set(res.data ?? null),
        error: () => this.reportLoading.set(false),
        complete: () => this.reportLoading.set(false),
      });
  }

  /** Valyuta almashganda faqat foyda hisoboti qayta o'qiladi. */
  onReportCurrencyChange(code: string) {
    this.selectedReportCurrency.set(code);
    this.loadReport();
  }

  onSearch() {
    this.loadOrders({ first: 0, rows: this.pageSize });
    this.loadReport();
  }

  // ── Row menu ───────────────────────────────────────────────────────────────

  openRowMenu(event: Event, menu: Menu, order: OrderDto) {
    this.rowMenuItems = [
      {
        label: this.translate.instant('ORDERS.ROW_MENU.VIEW'),
        icon: 'pi pi-eye',
        command: () => this.openDetails(order.id),
      },
      {
        label: this.translate.instant('ORDERS.REPRINT'),
        icon: 'pi pi-print',
        command: () => this.openReceipt(order.id),
      },
    ];
    if (this.canReturn(order.status)) {
      this.rowMenuItems.push({
        label: this.translate.instant('ORDERS.ROW_MENU.RETURN'),
        icon: 'pi pi-replay',
        command: () => this.openReturn(order.id),
      });
    }
    menu.toggle(event);
  }

  private canReturn(status: string): boolean {
    return status !== 'Cancelled' && status !== 'Returned';
  }

  // ── Return dialog ──────────────────────────────────────────────────────────

  openReturn(id: string) {
    this.returnOrder.set(null);
    this.orderSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.returnOrder.set(res.data);
            this.returnVisible.set(true);
          }
        },
      });
  }

  onReturned() {
    this.loadOrders(this.lastLazyEvent);
    this.loadReport();
    const current = this.selectedOrder();
    if (this.detailsVisible && current?.id) {
      this.orderSvc
        .getById(current.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ next: (res) => this.selectedOrder.set(res.data ?? null) });
    }
  }

  // ── Details dialog ─────────────────────────────────────────────────────────

  openDetails(id: string) {
    this.selectedOrder.set(null);
    this.detailsLoading = true;
    this.detailsVisible = true;

    this.orderSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.selectedOrder.set(res.data ?? null),
        error: () => {
          this.detailsLoading = false;
        },
        complete: () => {
          this.detailsLoading = false;
        },
      });
  }

  closeDetails() {
    this.detailsVisible = false;
    this.selectedOrder.set(null);
  }

  openReceipt(id: string) {
    this.orderSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!res.data) return;
          this.receiptOrderId.set(id);
          this.receipt.set(this.toReceiptData(res.data));
          this.receiptVisible.set(true);
          // PDF darhol olinadi — "Ulashish" bosilganda `navigator.share()` bosish
          // aktivatsiyasi ichida sinxron chaqirilishi kerak (servisga qarang).
          this.receiptExport.prefetch(id);
        },
      });
  }

  printReceipt(): void {
    this.receiptPrinter.print();
  }

  downloadReceiptPdf(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.exportBusy()) return;

    this.exportBusy.set(true);
    this.receiptExport
      .downloadPdf(orderId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.exportBusy.set(false)),
      )
      .subscribe();
  }

  shareReceipt(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.shareBusy()) return;

    this.shareBusy.set(true);
    this.receiptExport
      .sharePdf(orderId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.shareBusy.set(false)),
      )
      .subscribe();
  }

  private toReceiptData(order: OrderDetailDto): ReceiptData {
    const paymentTypes: Record<string, ReceiptPaymentType> = { Cash: 0, Card: 1, Debt: 2 };
    const subtotal = order.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    const discount = order.items.reduce((sum, item) => sum + item.discount * item.quantity, 0);
    const paymentType = paymentTypes[order.paymentType] ?? 0;

    return {
      receiptNumber: order.receiptNumber ?? order.id,
      storeName: order.storeName,
      cashierName: order.cashierName,
      date: order.orderDate,
      items: order.items.map((item, index) => ({
        id: `${item.productId}-${item.productVariantId ?? ''}-${index}`,
        productId: item.productId,
        productVariantId: item.productVariantId,
        productName: item.variantLabel
          ? `${item.productName} (${item.variantLabel})`
          : item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
      })),
      subtotal,
      discount,
      total: order.totalAmount,
      paymentType,
      paidAmount: order.paidAmount,
      change: paymentType === 0 ? Math.max(0, order.paidAmount - order.totalAmount) : 0,
      // Qayta chop etishda to'lov valyutasi order valyutasi bilan bir xil deb
      // olinadi: `Order` da to'lov valyutasi saqlanmaydi, u `OrderPayment` da.
      // To'lov tafsiloti kerak bo'lsa Bosqich 4c da chekka qo'shiladi.
      currency: order.currency,
      payCurrency: order.currency,
      payRate: 1,
      debtCustomerName: order.debtCustomerName,
    };
  }

  /** Detal: chegirmadan oldingi asl summa = Σ(unitPrice × qolgan miqdor). */
  detailOriginalTotal(): number {
    const o = this.selectedOrder();
    if (!o) return 0;
    return o.items.reduce(
      (sum, i) => sum + i.unitPrice * Math.max(0, i.quantity - (i.returnedQuantity ?? 0)),
      0,
    );
  }

  /** Detal: qolgan mahsulotlar bo'yicha chegirma = Σ(discount × qolgan miqdor). */
  detailTotalDiscount(): number {
    const o = this.selectedOrder();
    if (!o) return 0;
    return o.items.reduce(
      (sum, i) => sum + i.discount * Math.max(0, i.quantity - (i.returnedQuantity ?? 0)),
      0,
    );
  }

  /** Qator bo'yicha qolgan miqdor. */
  /**
   * Qaytarilgandan keyin qolgan miqdor.
   *
   * Ayirma bazadagi miqdor aniqligiga (`decimal(18,6)`) keltiriladi: `1 - 0.999`
   * suzuvchi nuqtada `0.0009999999999999432` beradi va ekranda o'sha ko'rinardi.
   * Bu YAXLITLASH emas — shovqinni kesish; 6-xonagacha hamma raqam saqlanadi.
   */
  itemRemainingQty(item: OrderDetailDto['items'][number]): number {
    const remaining = item.quantity - (item.returnedQuantity ?? 0);
    return Math.max(0, Number(remaining.toFixed(this.QUANTITY_DECIMALS)));
  }

  /** Bazadagi miqdor aniqligi — `OrderItem.Quantity` = `decimal(18,6)`. */
  private readonly QUANTITY_DECIMALS = 6;

  /**
   * Qator bo'yicha qolgan subtotal (qaytarishdan keyin).
   *
   * Natija valyuta aniqligida yaxlitlanadi — `0.999 × 4.4234` kabi ko'paytma
   * suzuvchi nuqta dumini qoldiradi va u `| money` orqali ekranga chiqardi.
   */
  itemNetSubTotal(item: OrderDetailDto['items'][number]): number {
    const remaining = this.itemRemainingQty(item);
    if (remaining <= 0) return 0;
    const currency = this.selectedOrder()?.currency;
    return this.currencySvc.roundPrice((item.unitPrice - item.discount) * remaining, currency);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'PartiallyPaid':
        return 'warn';
      case 'Pending':
        return 'warn';
      case 'Debt':
        return 'danger';
      case 'Cancelled':
        return 'danger';
      case 'Returned':
        return 'danger';
      case 'PartiallyReturned':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'Paid':
        return this.translate.instant('ORDERS.STATUS.PAID');
      case 'PartiallyPaid':
        return this.translate.instant('ORDERS.STATUS.PARTIALLY_PAID');
      case 'Pending':
        return this.translate.instant('ORDERS.STATUS.PENDING');
      case 'Debt':
        return this.translate.instant('ORDERS.STATUS.DEBT');
      case 'Cancelled':
        return this.translate.instant('ORDERS.STATUS.CANCELLED');
      case 'Returned':
        return this.translate.instant('ORDERS.STATUS.RETURNED');
      case 'PartiallyReturned':
        return this.translate.instant('ORDERS.STATUS.PARTIALLY_RETURNED');
      default:
        return status;
    }
  }

  getPaymentLabel(type: string): string {
    switch (type) {
      case 'Cash':
        return this.translate.instant('ORDERS.PAYMENT_TYPES.CASH');
      case 'Card':
        return this.translate.instant('ORDERS.PAYMENT_TYPES.CARD');
      case 'Debt':
        return this.translate.instant('ORDERS.PAYMENT_TYPES.DEBT');
      default:
        return type;
    }
  }

  getPaymentSeverity(type: string): 'success' | 'info' | 'warn' | 'secondary' {
    switch (type) {
      case 'Cash':
        return 'success';
      case 'Card':
        return 'info';
      case 'Debt':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  private toIso(date: Date | null): string | undefined {
    if (!date) return undefined;
    return date.toISOString();
  }
}
